import { apiClient, getErrorMessage } from '@/lib/apiClient';
import type {
  SearchMetadata,
  QuickSearchOptions,
  QuickSearchResult,
  AdvancedSearchOptions,
  AdvancedSearchResult,
  RecentSearch,
  RecordSelectionRequest,
} from '@/types/core/Search';

/**
 * Global Search Service
 * Provides methods for quick search, advanced search, and search history
 */
export class SearchService {
  private readonly baseUrl = '/api/search';

  /**
   * Get search metadata including searchable entities and shortcuts
   */
  async getMetadata(): Promise<SearchMetadata> {
    try {
      const response = await apiClient.get<SearchMetadata>(`${this.baseUrl}/metadata`);
      return response.data;
    } catch (error) {
      console.error('Error fetching search metadata:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Perform quick search across all entity types
   */
  async quickSearch(options: QuickSearchOptions): Promise<QuickSearchResult> {
    try {
      const params = new URLSearchParams();
      params.append('q', options.query);

      if (options.entityTypes && options.entityTypes.length > 0) {
        options.entityTypes.forEach(et => params.append('entityTypes', et));
      }

      if (options.maxResultsPerEntity) {
        params.append('maxResultsPerEntity', options.maxResultsPerEntity.toString());
      }

      const response = await apiClient.get<QuickSearchResult>(
        `${this.baseUrl}/quick?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error performing quick search:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Perform advanced search with filters and pagination
   */
  async advancedSearch(options: AdvancedSearchOptions): Promise<AdvancedSearchResult> {
    try {
      const params = new URLSearchParams();
      params.append('q', options.query);

      if (options.entityType) {
        params.append('entityType', options.entityType);
      }

      if (options.skip !== undefined) {
        params.append('skip', options.skip.toString());
      }

      if (options.take !== undefined) {
        params.append('take', options.take.toString());
      }

      if (options.sortField) {
        params.append('sortField', options.sortField);
      }

      if (options.sortDirection) {
        params.append('sortDirection', options.sortDirection);
      }

      // Filters are passed as JSON in a single parameter
      if (options.filters && Object.keys(options.filters).length > 0) {
        params.append('filters', JSON.stringify(options.filters));
      }

      const response = await apiClient.get<AdvancedSearchResult>(
        `${this.baseUrl}?${params.toString()}`
      );
      return response.data;
    } catch (error) {
      console.error('Error performing advanced search:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Get recent searches for the current user
   */
  async getRecentSearches(count: number = 5): Promise<RecentSearch[]> {
    try {
      const response = await apiClient.get<RecentSearch[]>(
        `${this.baseUrl}/recent?count=${count}`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching recent searches:', error);
      throw new Error(getErrorMessage(error));
    }
  }

  /**
   * Record a search selection for history tracking
   */
  async recordSelection(request: RecordSelectionRequest): Promise<void> {
    try {
      await apiClient.post(`${this.baseUrl}/record-selection`, request);
    } catch (error) {
      // Don't throw - recording selection is non-critical
      console.error('Error recording search selection:', error);
    }
  }
}

// Export singleton instance
export const searchService = new SearchService();
